import { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AppContext } from '@/app.context';
import { Issue } from '@/constants';
import {
	confirmRemoveDownloadedIssue,
	removeDownloadedIssue,
} from '@entities/downloaded-issue';
import { useServiceWorker } from '@shared/lib/hooks';
import { Logger } from '@shared/lib/logger';
import { translate } from '@shared/i18n';
import { contentOf, track } from '@shared/events';

const logger = new Logger('use-downloaded-issues');

/**
 * Reads the downloaded-issues list straight from Dexie -- no TanStack
 * Query involved, deliberately: this list *is* the offline store, not a
 * cache of server data, so there's nothing to key a query on and no server
 * response to go stale against (same reasoning `entities/favorite` never
 * routes favorites through Query either).
 */
export function useDownloadedIssues(): UseDownloadedIssuesResult {
	const { db } = useContext(AppContext);
	const { unpinIssueAssets } = useServiceWorker();
	const [issues, setIssues] = useState<Array<Issue> | null>(null);
	const [isRemoving, setIsRemoving] = useState<string | null>(null);

	const refresh = useCallback(() => {
		if (!db) {
			return;
		}
		db.getIssues()
			.then(setIssues)
			.catch((error: unknown) => {
				logger.error('Failed to load downloaded issues', error);
				setIssues([]);
			});
	}, [db]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const remove = useCallback(
		(issueId: string, { confirmed = false }: { confirmed?: boolean } = {}) => {
			if (!db) {
				return;
			}
			const run = () => {
				setIsRemoving(issueId);
				removeDownloadedIssue(db, issueId)
					.then(() => {
						refresh();
						track(
							'issue_download_remove',
							{},
							contentOf({ issue: { id: issueId, name: '' } }),
						);
						toast.success(translate('downloadedIssue.removed'));
						// Same reasoning as DownloadIssueButton's own remove
						// path -- keeps the cached bytes, just lifts the
						// pressure-eviction exemption.
						unpinIssueAssets(issueId);
					})
					.catch((error: unknown) => {
						logger.error(
							'Failed to remove downloaded issue',
							error,
						);
						toast.error(translate('downloadedIssue.removeFailed'));
					})
					.finally(() => setIsRemoving(null));
			};
			// `confirmed`: the caller already asked (Settings confirms inline,
			// since clicking a toast from inside a modal counts as clicking
			// outside it and closes the modal).
			if (confirmed) {
				run();
			} else {
				confirmRemoveDownloadedIssue(run);
			}
		},
		[db, refresh, unpinIssueAssets],
	);

	return {
		// `null` (not yet read) is treated as loading, distinct from `[]`
		// (read, genuinely empty) so the empty-state message doesn't flash
		// before Dexie has actually answered.
		isLoading: issues === null,
		issues: issues ?? [],
		removingIssueId: isRemoving,
		remove,
	};
}

interface UseDownloadedIssuesResult {
	isLoading: boolean;
	issues: Array<Issue>;
	removingIssueId: string | null;
	remove: (issueId: string, options?: { confirmed?: boolean }) => void;
}
